'use client';

import * as React from 'react';
import { useFirestore, useAuth } from '@/firebase';
import { getHierarchyCache, HierarchyNode } from '@/lib/gallery-cache';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Folder, FolderOpen, ChevronRight, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';

interface BatchMoveDialogProps {
    open: boolean;
    onClose: () => void;
    sourceAlbumIds: string[];
    sourceMediaIds?: string[];
    onComplete: () => void;
}

export function BatchMoveDialog({
    open,
    onClose,
    sourceAlbumIds,
    sourceMediaIds = [],
    onComplete
}: BatchMoveDialogProps) {
    const firestore = useFirestore();
    const auth = useAuth();
    const { toast } = useToast();

    const [hierarchy, setHierarchy] = React.useState<HierarchyNode[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [selectedTarget, setSelectedTarget] = React.useState<{ id: string; name: string } | null>(null);

    // Load album hierarchy when dialog opens
    React.useEffect(() => {
        if (!open || !firestore) return;

        async function loadHierarchy() {
            setLoading(true);
            try {
                const role = auth.currentUser ? 'members' : 'public';
                let cache = await getHierarchyCache(firestore!, role);

                // Fallback to public if members cache fails
                if (!cache && role === 'members') {
                    cache = await getHierarchyCache(firestore!, 'public');
                }

                setHierarchy(cache || []);
            } catch (error) {
                console.error('Error loading hierarchy:', error);
                toast({
                    variant: 'destructive',
                    title: 'Error',
                    description: 'Failed to load album list'
                });
            } finally {
                setLoading(false);
            }
        }

        loadHierarchy();
        setSelectedTarget(null);
    }, [open, firestore, auth.currentUser]);

    const handleConfirm = async () => {
        if (!firestore) return;

        // If no target selected, assume Root (or require explicit root selection? My API handles null as root)
        // But UI should probably be explicit. Let's assume user must select "Gallery Root" or a folder.
        // Actually, hierarchy usually has a "Root" node or list of top-levle nodes.
        // If they want to move to root, they deselect or select a "Root" pseudo-node.
        // For now, let's require a selection. If they want root, maybe I add a "Move to Root" button or pseudo node?
        // Let's rely on my API supporting null, but force user to pick something.
        // Wait, if I restrict selection, how do they move to top level?
        // I'll add a "Gallery Root" option manually at the top of the list or handle null.

        const targetId = selectedTarget?.id || null; // null means root if we execute that way, but let's restrict for safety unless explicit.

        // Actually, my API said "targetAlbumId" from body. If null, it moves to root.
        // Let's just pass `targetAlbumId: selectedTarget?.id || null`.

        setSaving(true);
        try {
            const token = await auth.currentUser?.getIdToken();
            const response = await fetch('/api/gallery/batch', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    operation: 'move',
                    sourceAlbumIds,
                    sourceMediaIds,
                    targetAlbumId: selectedTarget?.id || null
                })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Batch operation failed');
            }

            toast({
                title: 'Albums Moved',
                description: `Successfully moved ${sourceAlbumIds.length} albums.`
            });

            onComplete();
            onClose();
        } catch (error: any) {
            console.error('Error moving albums:', error);
            toast({
                variant: 'destructive',
                title: 'Move Failed',
                description: error.message || 'Failed to move albums'
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Move {sourceAlbumIds.length + sourceMediaIds.length} Items</DialogTitle>
                    <DialogDescription>
                        Select the destination folder.
                    </DialogDescription>
                </DialogHeader>

                <div className="py-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : (
                        <ScrollArea className="h-[300px] border rounded-md p-2">
                            {/* Root Option */}
                            <div
                                className={cn(
                                    "flex items-center gap-2 py-1.5 px-2 rounded-md cursor-pointer transition-colors mb-1",
                                    selectedTarget === null ? "bg-primary text-primary-foreground" : "hover:bg-accent"
                                )}
                                onClick={() => setSelectedTarget(null)}
                            >
                                <Folder className="w-4 h-4" />
                                <span className="text-sm font-medium">Gallery Root</span>
                            </div>

                            {hierarchy.map((node) => (
                                <AlbumTreeItem
                                    key={node.id}
                                    node={node}
                                    depth={0}
                                    selectedId={selectedTarget?.id}
                                    sourceAlbumIds={sourceAlbumIds}
                                    onSelect={(id, name) => setSelectedTarget({ id, name })}
                                />
                            ))}
                        </ScrollArea>
                    )}

                    <div className="mt-3 p-2 bg-muted rounded-md text-sm">
                        <span className="text-muted-foreground">Destination: </span>
                        <span className="font-medium">{selectedTarget ? selectedTarget.name : 'Gallery Root'}</span>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm} disabled={saving}>
                        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Move Albums
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

interface AlbumTreeItemProps {
    node: HierarchyNode;
    depth: number;
    selectedId?: string;
    sourceAlbumIds: string[];
    onSelect: (id: string, name: string) => void;
    disabledParent?: boolean; // If true, this node and children are disabled (is a source or child of source)
}

function AlbumTreeItem({ node, depth, selectedId, sourceAlbumIds, onSelect, disabledParent }: AlbumTreeItemProps) {
    const [expanded, setExpanded] = React.useState(depth < 1);
    const hasChildren = node.children && node.children.length > 0;
    const isSelected = node.id === selectedId;

    // Check if this node is one of the source albums
    const isSource = sourceAlbumIds.includes(node.id);
    const isDisabled = disabledParent || isSource;

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        setExpanded(!expanded);
    };

    const handleSelect = () => {
        if (!isDisabled) {
            onSelect(node.id, node.name);
        }
    };

    return (
        <div>
            <div
                className={cn(
                    'flex items-center gap-2 py-1.5 px-2 rounded-md transition-colors',
                    isSelected && 'bg-primary text-primary-foreground',
                    !isSelected && !isDisabled && 'hover:bg-accent cursor-pointer',
                    isDisabled && 'opacity-50 cursor-not-allowed'
                )}
                style={{ paddingLeft: `${depth * 16 + 8}px` }}
                onClick={handleSelect}
            >
                {hasChildren ? (
                    <button
                        onClick={handleToggle}
                        className="p-0.5 hover:bg-accent-foreground/10 rounded"
                    >
                        {expanded ? (
                            <ChevronDown className="w-4 h-4" />
                        ) : (
                            <ChevronRight className="w-4 h-4" />
                        )}
                    </button>
                ) : (
                    <span className="w-5" />
                )}

                {expanded ? (
                    <FolderOpen className="w-4 h-4 flex-shrink-0" />
                ) : (
                    <Folder className="w-4 h-4 flex-shrink-0" />
                )}

                <span className="text-sm truncate flex-1">{node.name}</span>

                {isSource && (
                    <span className="text-xs text-muted-foreground ml-2">(moving)</span>
                )}
            </div>

            {expanded && hasChildren && (
                <div>
                    {node.children!.map((child) => (
                        <AlbumTreeItem
                            key={child.id}
                            node={child}
                            depth={depth + 1}
                            selectedId={selectedId}
                            sourceAlbumIds={sourceAlbumIds}
                            onSelect={onSelect}
                            disabledParent={isDisabled}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
